import { useMemo } from 'react';
import { Box } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { getLeaveTypeColor } from '../utils/leaveTypeColors';

export interface LeaveReportEntryForChart {
  employeeId: string;
  employeeName: string;
  leaveTypeName: string;
  daysTaken: number;
}

interface LeaveBarChartProps {
  entries: LeaveReportEntryForChart[];
  height?: number;
}

/**
 * Stacked bar chart: one bar per employee, each segment = leave type (days taken).
 */
export default function LeaveBarChart({ entries, height = 400 }: LeaveBarChartProps) {
  const { chartData, leaveTypeKeys } = useMemo(() => {
    const byEmployee = new Map<
      string,
      { employeeName: string; [leaveType: string]: string | number }
    >();
    const types = new Set<string>();

    for (const e of entries) {
      if (!e.daysTaken) continue;
      types.add(e.leaveTypeName);
      const key = e.employeeId;
      const row = byEmployee.get(key);
      if (!row) {
        byEmployee.set(key, {
          employeeName: e.employeeName,
          [e.leaveTypeName]: e.daysTaken,
        });
      } else {
        const current = (row[e.leaveTypeName] as number) ?? 0;
        row[e.leaveTypeName] = current + e.daysTaken;
      }
    }

    const data = Array.from(byEmployee.values())
      .map((row) => {
        const out: Record<string, string | number> = { employeeName: row.employeeName };
        for (const t of types) {
          out[t] = (row[t] as number) ?? 0;
        }
        return out;
      })
      .sort((a, b) => {
        const sumA = Array.from(types).reduce((s, t) => s + ((a[t] as number) ?? 0), 0);
        const sumB = Array.from(types).reduce((s, t) => s + ((b[t] as number) ?? 0), 0);
        return sumB - sumA;
      });

    return {
      chartData: data,
      leaveTypeKeys: Array.from(types).sort(),
    };
  }, [entries]);

  if (chartData.length === 0 || leaveTypeKeys.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', minHeight: height, height: `${height}px`, overflow: 'hidden' }}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          layout="vertical"
          barCategoryGap="12%"
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="employeeName"
            width={200}
            tick={{ fontSize: 12, style: { whiteSpace: 'nowrap' } }}
          />
          <Tooltip
            formatter={(value: number, name: string) => [value, name]}
            labelFormatter={(label) => `Employee: ${label}`}
          />
          <Legend />
          {leaveTypeKeys.map((key) => (
            <Bar key={key} dataKey={key} stackId="days" fill={getLeaveTypeColor(key)} name={key} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
